import React from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { prepareReportChartData, generateEmotionTrendData } from '../utils/doctorDashboardUtils';
import { calculateOverallMentalHealthScore } from '../utils/mentalStateCalculations';
import { EmotionTrendsChart, RiskAnalysisPanel, MentalStateDisplay } from './DoctorReportWidgets';

function generateHealthySummary(patient, emotionScore, weeklyTrend, monthlyTrend) {
  const joyLevel = patient.emotionVector.joy || 0;
  const stressLevel = patient.emotionVector.stress || 0;

  if (joyLevel > 60 && stressLevel < 20) {
    return "Patient demonstrates excellent emotional well-being with high joy and minimal stress levels.";
  } else if (weeklyTrend > 10) {
    return "Patient shows strong positive trends with significant improvement in mental health indicators.";
  } else {
    return "Patient maintains healthy mental state with balanced emotions and low risk factors.";
  }
}

function generateStableSummary(patient, emotionScore, weeklyTrend, monthlyTrend) {
  const positiveEmotions = Object.entries(patient.emotionVector)
    .filter(([emotion, value]) => emotion === 'joy' && value > 30)
    .length;

  if (positiveEmotions > 0) {
    return "Patient is showing consistent improvement in emotional stability with reduced stress levels.";
  } else if (weeklyTrend > 0) {
    return "Patient demonstrates stable mental health with positive weekly progress trends.";
  } else {
    return "Patient maintains stable mental state with balanced emotional indicators.";
  }
}

function generateDecliningSummary(patient, emotionScore, weeklyTrend, monthlyTrend) {
  if (patient.emotionVector.stress > 50) {
    return "High stress levels detected, suggesting a declining mental state.";
  } else if (patient.cumulativeRisk > 60) {
    return "Elevated risk factors indicate declining mental health status.";
  } else if (weeklyTrend < 0) {
    return "Recent decline in weekly progress suggests deteriorating mental state.";
  } else {
    return "Patient shows concerning signs with increased negative emotional indicators.";
  }
}

function generateCriticalSummary(patient, emotionScore, weeklyTrend, monthlyTrend) {
  if (patient.cumulativeRisk > 80) {
    return "Critical risk levels detected - immediate intervention recommended.";
  } else if (patient.emotionVector.stress > 70 || patient.emotionVector.sadness > 60) {
    return "Severe emotional distress detected - urgent mental health support needed.";
  } else if (weeklyTrend < -10) {
    return "Rapid decline in mental health indicators - critical intervention required.";
  } else {
    return "Multiple risk factors at critical levels - immediate professional attention needed.";
  }
}

function calculateMentalState(patient) {
  const emotionWeights = {
    joy: 0.4,
    stress: -0.3,
    sadness: -0.2,
    anger: -0.2,
    hurt: -0.1
  };

  const emotionScore = Object.entries(patient.emotionVector || {}).reduce((score, [emotion, value]) => {
    const weight = emotionWeights[emotion] || 0;
    const numValue = Number(value) || 0;
    const contribution = (numValue / 100) * weight;
    return score + (isNaN(contribution) ? 0 : contribution);
  }, 0);

  const weeklyTrend = patient.weeklyReport.length >= 2
    ? (typeof patient.weeklyReport[patient.weeklyReport.length - 1] === 'object'
      ? patient.weeklyReport[patient.weeklyReport.length - 1].score
      : patient.weeklyReport[patient.weeklyReport.length - 1]) -
    (typeof patient.weeklyReport[0] === 'object'
      ? patient.weeklyReport[0].score
      : patient.weeklyReport[0])
    : 0;

  const monthlyTrend = patient.monthlyReport.length >= 2
    ? (typeof patient.monthlyReport[patient.monthlyReport.length - 1] === 'object'
      ? patient.monthlyReport[patient.monthlyReport.length - 1].score
      : patient.monthlyReport[patient.monthlyReport.length - 1]) -
    (typeof patient.monthlyReport[0] === 'object'
      ? patient.monthlyReport[0].score
      : patient.monthlyReport[0])
    : 0;

  const baseScore = Number(patient.overallMark) || 70;
  const trendAdjustment = (Number(weeklyTrend) || 0) * 0.3 + (Number(monthlyTrend) || 0) * 0.2;
  const emotionAdjustment = (Number(emotionScore) || 0) * 100;
  const riskPenalty = (Number(patient.cumulativeRisk) || 0) * 0.3;

  const finalScore = Math.max(0, Math.min(100, baseScore + trendAdjustment + emotionAdjustment - riskPenalty));

  if (isNaN(finalScore) || !isFinite(finalScore)) {
    console.warn('⚠️ INVALID MENTAL STATE SCORE, using fallback:', finalScore);
    return {
      score: 50,
      state: "Unknown",
      color: "#6b7280",
      summary: "Unable to calculate mental state - insufficient data available."
    };
  }

  let state, color, summary;
  const burnout = patient.riskComponents?.burnout || 0;

  if (finalScore >= 70 && burnout < 30) {
    state = "Healthy";
    color = "#28a745";
    summary = generateHealthySummary(patient, emotionScore, weeklyTrend, monthlyTrend);
  } else if (finalScore >= 50 && burnout < 50) {
    state = "Stable";
    color = "#17a2b8";
    summary = generateStableSummary(patient, emotionScore, weeklyTrend, monthlyTrend);
  } else if (finalScore >= 30 && burnout < 70) {
    state = "Declining";
    color = "#fd7e14";
    summary = generateDecliningSummary(patient, emotionScore, weeklyTrend, monthlyTrend);
  } else {
    state = "Critical";
    color = "#dc3545";
    summary = generateCriticalSummary(patient, emotionScore, weeklyTrend, monthlyTrend);
  }

  return { state, color, summary, score: finalScore };
}

export default function ReportModal({ patient, onClose, onStartSession, sessionHistory, latestSessionNote }) {
  if (!patient) return null;

  if (!patient.hasSessionData) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Report - {patient.name}</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="new-patient-message">
              <div className="new-patient-icon">👤</div>
              <h3>New Patient</h3>
              <p>No session data available yet for {patient.name}.</p>
              <p>Reports and analytics will be available after the patient completes their first session.</p>
              <div className="new-patient-actions">
                <button
                  className="btn-primary"
                  onClick={() => {
                    onClose();
                    onStartSession();
                  }}
                >
                  Start First Session
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { emotionData, weeklyData, monthlyData } = prepareReportChartData(patient, calculateOverallMentalHealthScore);
  const emotionTrendData = generateEmotionTrendData(sessionHistory);
  const mentalState = calculateMentalState(patient);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Patient Report - {patient.name}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="report-grid">
            {/* Emotion Vector Chart */}
            <div className="chart-section radar-card radar-flat">
              <h3>Emotion Vector</h3>
              <ResponsiveContainer width="100%" height={340}>
                <RadarChart
                  data={emotionData}
                  cx="50%"
                  cy="50%"
                  outerRadius="84%"
                  startAngle={225}
                  endAngle={585}
                  margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
                >
                  <defs>
                    <linearGradient id="evBlueGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#6f8dff" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#5a78ff" stopOpacity={0.35} />
                    </linearGradient>
                    <filter id="evSoftGlow" x="-40%" y="-40%" width="180%" height="180%">
                      <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  <PolarGrid
                    gridType="polygon"
                    stroke="#d8d8d8"
                    strokeOpacity={0.6}
                    radialLines={true}
                  />
                  <PolarAngleAxis
                    dataKey="name"
                    tick={{
                      fill: '#7a7a7a',
                      fontSize: 13,
                      fontWeight: 400,
                      fontFamily: 'Inter, Poppins, Roboto, system-ui, -apple-system, Segoe UI, Arial, sans-serif'
                    }}
                    tickMargin={10}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 80]}
                    tick={false}
                  />
                  <Radar
                    name="Emotions"
                    dataKey="value"
                    stroke="rgba(84,120,255,0.9)"
                    strokeWidth={2.5}
                    fill="url(#evBlueGrad)"
                    fillOpacity={0.45}
                    filter="url(#evSoftGlow)"
                    isAnimationActive={true}
                    animationBegin={100}
                    animationDuration={1000}
                    animationEasing="ease-out"
                  />
                  <Tooltip
                    wrapperStyle={{ outline: 'none' }}
                    contentStyle={{
                      borderRadius: 8,
                      background: 'rgba(255,255,255,0.96)',
                      border: '1px solid #e5e7eb',
                      boxShadow: 'none',
                      padding: '6px 10px',
                      fontFamily: 'Inter, Poppins, Roboto, sans-serif',
                      fontSize: 12,
                      color: '#374151'
                    }}
                    formatter={(value) => [`${Math.round(value)}%`, '']}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Weekly Report */}
            <div className="chart-section">
              <div className="chart-header">
                <h3>Weekly Report</h3>
              </div>
              {patient.hasWeeklyData !== false && weeklyData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={weeklyData}>
                      <defs>
                        <linearGradient id="weeklyGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#4f46e5" />
                          <stop offset="100%" stopColor="#8b5cf6" />
                        </linearGradient>
                        <filter id="weeklyGlow">
                          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                          <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                          </feMerge>
                        </filter>
                      </defs>
                      {(() => {
                        const isDevice = typeof document !== 'undefined' && document.body?.getAttribute('data-theme') === 'device';
                        const axisTick = { fontSize: 12, fill: isDevice ? '#ffffff' : '#6b7280' };
                        const gridStroke = isDevice ? '#3a3a3a' : '#e5e7eb';
                        return (
                          <>
                            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                            <XAxis dataKey="day" tick={axisTick} />
                            <YAxis tick={axisTick} domain={[0, 100]} />
                          </>
                        );
                      })()}
                      {(() => {
                        const isDevice = typeof document !== 'undefined' && document.body?.getAttribute('data-theme') === 'device';
                        const contentStyle = isDevice ? {
                          backgroundColor: 'rgba(0,0,0,0.85)',
                          border: '1px solid #333',
                          borderRadius: '8px',
                          boxShadow: '0 8px 18px rgba(0,0,0,0.45)',
                          color: '#ffffff'
                        } : {
                          backgroundColor: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        };
                        const wrapperStyle = isDevice ? { outline: 'none', background: 'transparent' } : { outline: 'none' };
                        return (
                          <Tooltip
                            wrapperStyle={wrapperStyle}
                            contentStyle={contentStyle}
                            formatter={(value, name, props) => {
                              const emotions = props.payload.emotions;
                              const sessionDate = props.payload.sessionDate;
                              if (emotions && sessionDate) {
                                return [
                                  <div key="tooltip">
                                    <div><strong>Overall Mental Health Score: {Math.round(value)}%</strong></div>
                                    <div>Joy: {Math.round(emotions.joy || 0)}%</div>
                                    <div>Stress: {Math.round(emotions.stress || 0)}%</div>
                                    <div>Sadness: {Math.round(emotions.sadness || 0)}%</div>
                                    <div>Anger: {Math.round(emotions.anger || 0)}%</div>
                                    <div>Hurt: {Math.round(emotions.hurt || 0)}%</div>
                                    <div>Burnout: {Math.round(emotions.burnout_score || 0)}%</div>
                                  </div>
                                ];
                              } else {
                                return [
                                  <div key="tooltip">
                                    <div><strong>No Session Data</strong></div>
                                    <div>Overall Mental Health Score: 0%</div>
                                    <div>No sessions conducted on this day</div>
                                  </div>
                                ];
                              }
                            }}
                          />
                        );
                      })()}
                      <Line
                        type="monotone"
                        dataKey="score"
                        stroke="url(#weeklyGradient)"
                        strokeWidth={3}
                        dot={{ r: 6, fill: '#4f46e5', stroke: '#ffffff', strokeWidth: 2 }}
                        activeDot={{ r: 8, fill: '#8b5cf6', stroke: '#ffffff', strokeWidth: 2 }}
                        filter="url(#weeklyGlow)"
                        animationBegin={0}
                        animationDuration={1500}
                        animationEasing="ease-out"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="chart-legend">
                    <span className="legend-item">
                      <span className="legend-color" style={{ background: 'linear-gradient(45deg, #4f46e5, #8b5cf6)' }}></span>
                      Overall Mental Health Score (weighted calculation from all emotions)
                    </span>
                  </div>
                </>
              ) : (
                <div className="no-data-message">
                  <div className="no-data-icon">📊</div>
                  <h4>No Weekly Data Available</h4>
                  <p>This patient hasn't completed any sessions yet. Weekly trends will appear here once sessions are conducted.</p>
                </div>
              )}
            </div>

            {/* Monthly Report */}
            <div className="chart-section">
              <div className="chart-header">
                <h3>Monthly Report</h3>
              </div>
              {patient.hasMonthlyData !== false && monthlyData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyData}>
                      <defs>
                        <linearGradient id="monthlyGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" />
                          <stop offset="100%" stopColor="#34d399" />
                        </linearGradient>
                        <filter id="monthlyGlow">
                          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                          <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                          </feMerge>
                        </filter>
                      </defs>
                      {(() => {
                        const isDevice = typeof document !== 'undefined' && document.body?.getAttribute('data-theme') === 'device';
                        const axisTick = { fontSize: 12, fill: isDevice ? '#ffffff' : '#6b7280' };
                        const gridStroke = isDevice ? '#3a3a3a' : '#e5e7eb';
                        return (
                          <>
                            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                            <XAxis dataKey="month" tick={axisTick} />
                            <YAxis tick={axisTick} domain={[0, 100]} />
                          </>
                        );
                      })()}
                      {(() => {
                        const isDevice = typeof document !== 'undefined' && document.body?.getAttribute('data-theme') === 'device';
                        const contentStyle = isDevice ? {
                          backgroundColor: 'rgba(0,0,0,0.85)',
                          border: '1px solid #333',
                          borderRadius: '8px',
                          boxShadow: '0 8px 18px rgba(0,0,0,0.45)',
                          color: '#ffffff'
                        } : {
                          backgroundColor: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        };
                        const wrapperStyle = isDevice ? { outline: 'none', background: 'transparent' } : { outline: 'none' };
                        return (
                          <Tooltip
                            wrapperStyle={wrapperStyle}
                            contentStyle={contentStyle}
                            formatter={(value, name, props) => {
                              const emotions = props.payload.emotions;
                              const sessionCount = props.payload.sessionCount;
                              if (emotions && sessionCount > 0) {
                                return [
                                  <div key="tooltip">
                                    <div><strong>Average Overall Mental Health Score: {Math.round(value)}%</strong></div>
                                    <div>Avg Joy: {Math.round(emotions.joy || 0)}%</div>
                                    <div>Avg Stress: {Math.round(emotions.stress || 0)}%</div>
                                    <div>Avg Sadness: {Math.round(emotions.sadness || 0)}%</div>
                                    <div>Avg Anger: {Math.round(emotions.anger || 0)}%</div>
                                    <div>Avg Hurt: {Math.round(emotions.hurt || 0)}%</div>
                                    <div>Avg Burnout: {Math.round(emotions.burnout_score || 0)}%</div>
                                    <div>Sessions: {sessionCount || 0}</div>
                                  </div>
                                ];
                              } else {
                                return [
                                  <div key="tooltip">
                                    <div><strong>No Session Data</strong></div>
                                    <div>Average Overall Mental Health Score: 0%</div>
                                    <div>Sessions: 0</div>
                                    <div>No sessions conducted in this month</div>
                                  </div>
                                ];
                              }
                            }}
                          />
                        );
                      })()}
                      <Bar
                        dataKey="score"
                        fill="url(#monthlyGradient)"
                        radius={[4, 4, 0, 0]}
                        filter="url(#monthlyGlow)"
                        animationBegin={0}
                        animationDuration={1200}
                        animationEasing="ease-out"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="chart-legend">
                    <span className="legend-item">
                      <span className="legend-color" style={{ background: 'linear-gradient(45deg, #10b981, #34d399)' }}></span>
                      Average Monthly Overall Mental Health Score
                    </span>
                  </div>
                </>
              ) : (
                <div className="no-data-message">
                  <div className="no-data-icon">📈</div>
                  <h4>No Monthly Data Available</h4>
                  <p>This patient hasn't completed any sessions yet. Monthly trends will appear here once sessions are conducted.</p>
                </div>
              )}
            </div>

            {/* Emotion Trends from Session History */}
            <EmotionTrendsChart data={emotionTrendData} />

            {/* Risk Analysis */}
            <div className="risk-analysis">
              <h3>Risk Analysis</h3>
              <RiskAnalysisPanel
                riskComponents={patient.riskComponents}
                cumulativeRisk={patient.cumulativeRisk}
                healthStatus={patient.healthStatus}
              />
            </div>

            {/* Mental State */}
            <MentalStateDisplay mentalState={mentalState} />

            {/* Latest Session Note (replaces Burnout card) */}
            {sessionHistory && sessionHistory.length > 0 && (
              <div className="burnout-display">
                <h3>Session Note (Latest Session)</h3>
                {latestSessionNote ? (
                  <div className="note-text" style={{ fontStyle: 'italic' }}>{latestSessionNote}</div>
                ) : (
                  <div className="note-text muted">No note available for the latest session</div>
                )}
                <p className="muted">From latest session: {sessionHistory[0].session_date ? new Date(sessionHistory[0].session_date).toLocaleDateString() : 'N/A'}</p>
              </div>
            )}

            {/* Clinical Assessment from Latest Session */}
            {sessionHistory && sessionHistory.length > 0 && sessionHistory[0].emotion && sessionHistory[0].emotion.doctor_assessment && (
              <div className="clinical-assessment-display">
                <h3>Clinical Assessment by Doctor</h3>
                <div className="assessment-badge">
                  {sessionHistory[0].emotion.doctor_assessment === 'Stable' && '🟢 Stable'}
                  {sessionHistory[0].emotion.doctor_assessment === 'Moderate Risk' && '🟠 Moderate Risk'}
                  {sessionHistory[0].emotion.doctor_assessment === 'High Risk' && '🔴 High Risk'}
                </div>
                <p className="muted">From latest session: {sessionHistory[0].session_date ? new Date(sessionHistory[0].session_date).toLocaleDateString() : 'N/A'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
